import { subscribeToNewsletter, unsubscribeFromNewsletter } from '../../newsletter/services/NewsletterService';
import React, { useEffect, useState, useCallback } from 'react';
import { List } from '../../newsletter/types/Newsletter';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useTranslations } from '../../localization/useTranslations';
import RegularNewslettersSection from '../../newsletter-landing/components/RegularNewslettersSection';
import PremiumNewslettersSection from '../../newsletter-landing/components/PremiumNewslettersSection';
import PremiumNewsletterModal from '../../newsletter-landing/components/PremiumNewsletterModal';
import { fetchNewsletters, fetchSubscribedNewsletters } from '../../newsletter-landing/services/NewsletterPageService';

interface NewsletterListProps {
  thankyouNewsletterId?: number; // Optional prop to filter for a specific free newsletter used on thankyou pages
}

const NewsletterList: React.FC<NewsletterListProps> = ({ thankyouNewsletterId }) => {
  const translations = useTranslations();
  const [newsletters, setNewsletters] = useState<List[] | null>(null);
  const [premiumNewsletters, setPremiumNewsletters] = useState<List[] | null>(null);
  const [subscribedNewsletters, setSubscribedNewsletters] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

  const openSnackbar = useCallback((message: string) => {
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
  }, []);

  const onCloseSnackbar = () => {
    setIsSnackbarOpen(false);
  };

  const checkIsSubscribed = (newsletter: List) => {
    return subscribedNewsletters.includes(newsletter.code);
  };

  const handleNewsletterChange = async (newsletter: List) => {
    const isSubscribed = checkIsSubscribed(newsletter);

    try {
      if (isSubscribed) {
        const response = await unsubscribeFromNewsletter(newsletter.code);

        if (response.status === 'ok') {
          openSnackbar(
            translations?.snackBarMessages?.unsubscribedFromNewsletter || 'You have unsubscribed from this newsletter'
          );

          setSubscribedNewsletters(subscribedNewsletters.filter((code) => code !== newsletter.code));
        }
      } else {
        const response = await subscribeToNewsletter(newsletter.code);

        if (response.status === 'ok') {
          openSnackbar(
            translations?.snackBarMessages?.subscribedToNewsletter || 'You have subscribed to this newsletter'
          );

          setSubscribedNewsletters([...subscribedNewsletters, newsletter.code]);
        }
      }
    } catch (error) {
      console.error(error);
      openSnackbar(
        translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again later.'
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { regularList, premiumList } = await fetchNewsletters();
        const subscribedCodes = await fetchSubscribedNewsletters();

        setNewsletters(regularList);
        setPremiumNewsletters(premiumList);
        setSubscribedNewsletters(subscribedCodes);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        openSnackbar(
          translations?.snackBarMessages?.somethingWentWrong || 'Something went wrong. Please try again later.'
        );
      }
    };

    fetchData();
  }, [openSnackbar, translations]);

  if (isLoading) {
    return (
      <main className="page-newsletter__content" style={{ height: '100vh' }}>
        <div className="container" />
      </main>
    );
  }

  const modalContent = {
    ...window.premiumModalFields,
    header_background_image:
      typeof window.premiumModalFields?.header_background_image === 'string'
        ? window.premiumModalFields?.header_background_image
        : undefined,
  };

  // If thankyouNewsletterId exists, filter to display only that newsletter
  if (thankyouNewsletterId && newsletters) {
    const filteredNewsletter = newsletters.find((newsletter) => newsletter.id === thankyouNewsletterId);

    if (!filteredNewsletter) {
      return null;
    }

    return (
      <>
        <CustomSnackbar message={snackbarMessage} open={isSnackbarOpen} onClose={onCloseSnackbar} />

        {/* Display Single Newsletter Card */}
        <RegularNewslettersSection
          newsletters={[filteredNewsletter]} // Pass single newsletter as an array
          onNewsletterChange={handleNewsletterChange}
          checkIsSubscribed={checkIsSubscribed}
        />
      </>
    );
  }

  return (
    <>
      <CustomSnackbar message={snackbarMessage} open={isSnackbarOpen} onClose={onCloseSnackbar} />

      <div className="my-newsletters">
        <div className="container">
          {/* Regular Newsletters Section */}
          <RegularNewslettersSection
            newsletters={newsletters}
            onNewsletterChange={handleNewsletterChange}
            checkIsSubscribed={checkIsSubscribed}
          />
          {/* Premium Newsletters Section */}
          {premiumNewsletters && premiumNewsletters.length > 0 && (
            <PremiumNewslettersSection
              premiumNewsletters={premiumNewsletters}
              handlePremiumLinkClick={() => setIsDialogOpen(true)}
              onNewsletterChange={handleNewsletterChange}
              checkIsSubscribed={checkIsSubscribed}
              premiumLink={modalContent?.button_link}
            />
          )}

          {/* Premium Newsletter Modal */}
          {modalContent && (
            <PremiumNewsletterModal
              isOpen={isDialogOpen}
              onClose={() => setIsDialogOpen(false)}
              modalContent={modalContent}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default NewsletterList;
